/**
 * TableLoadingOverlay.tsx — 统计表格的统一加载遮罩。
 *
 * 数据在请求中时，在表格区域上盖一层半透明遮罩 + 圆环动画 + 文案，让运营明确知道「正在加载」，
 * 而不是对着空白或上一次的旧数据发呆。所有统计页经 `StateGate` 复用这一个组件。
 *
 * 实现要点：
 * - 遮罩 `position: absolute` 铺满容器，圆环放在靠上位置而不是正中：长表格的正中常在首屏之外
 * - 遮罩拦截点击，避免加载期间对着旧数据排序 / 展开
 * - `role="status"` + `aria-busy`，读屏会播报文案；动画走既有 `.spinner`，`prefers-reduced-motion` 下自动停转
 * - 容器用 `gap: inherit` 复刻父级 `.page` / `.vstack` 的纵向间距，包上它不改变子块之间的排版
 */

import type { ReactNode } from 'react';
import { Spinner } from '~/ui/Skeleton';

export const TABLE_LOADING_LABEL = '正在加载数据…';

export function TableLoadingOverlay({
  loading,
  label = TABLE_LOADING_LABEL,
  children,
}: {
  loading: boolean;
  label?: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 'inherit',
        minWidth: 0,
      }}
    >
      {children}
      {loading && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 72,
            background: 'color-mix(in srgb, var(--bg-elev) 66%, transparent)',
            borderRadius: 'var(--radius-md)',
            animation: 'fade-in 0.15s ease-out',
          }}
        >
          <span
            className="hstack"
            style={{
              gap: 8,
              padding: '8px 14px',
              borderRadius: 999,
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-md)',
              color: 'var(--fg-muted)',
              fontSize: 12.5,
            }}
          >
            <Spinner size={16} stroke={2} />
            {label}
          </span>
        </div>
      )}
    </div>
  );
}
